import { useState, useEffect } from 'react'
import { MultiSelectDropdown } from '../../components/ui/MultiSelectDropdown'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { useGetCategoriesQuery } from '../../store/api/categoriesApi'
import { useGetPreferencesQuery, useUpdatePreferencesMutation } from '../../store/api/usersApi'
import { useGetPublicLegalDocumentsQuery, useAgreeToTermsMutation } from '../../store/api/legalApi'
import { api, getErrorMessage } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'

type Step = 'welcome' | 'interests' | 'done'

export default function OnboardingModal() {
  const { user, isAuthenticated, accessToken } = useAppSelector((s) => s.auth)
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('welcome')
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([])
  const [visible, setVisible] = useState(false)

  const { data: prefsData, isSuccess: prefsLoaded } = useGetPreferencesQuery(undefined, { skip: !isAuthenticated })
  const [updatePreferences] = useUpdatePreferencesMutation()
  const { data: categoriesData } = useGetCategoriesQuery(undefined, { skip: !visible })
  const categories = categoriesData?.categories ?? []

  const { data: legalData } = useGetPublicLegalDocumentsQuery(undefined, { skip: !isAuthenticated })
  const [agreeToTerms, { isLoading: agreeing }] = useAgreeToTermsMutation()
  const [hasDownloaded, setHasDownloaded] = useState(false)
  const [agreedCheckbox, setAgreedCheckbox] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [legalError, setLegalError] = useState<string | null>(null)

  // Only true when a 'user' legal document has actually been uploaded and this
  // account hasn't agreed to it yet — otherwise the welcome step behaves as
  // before, so onboarding never gets stuck if the admin hasn't added one yet.
  const userDoc = legalData?.documents.find((d) => d.type === 'user')
  const needsAgreement = !!userDoc?.filename && !prefsData?.preferences?.termsAgreedAt

  // Server truth only — this must show on every load until the account is
  // actually marked complete, with no local/session bypass.
  useEffect(() => {
    if (!isAuthenticated || !user || !prefsLoaded) return
    const completedInDB = prefsData?.preferences?.onboardingCompleted === true
    setVisible(!completedInDB)
  }, [isAuthenticated, user, prefsLoaded, prefsData])

  // The only way this modal goes away — no temporary/session-only close.
  // It must reappear on every load until the account is actually complete.
  const finishOnboarding = () => {
    void updatePreferences({ onboardingCompleted: true })
    setVisible(false)
  }

  const handleDownload = async () => {
    setDownloading(true); setLegalError(null)
    try {
      const { blob, filename } = await api.downloadFile('users', ENDPOINTS.legal.download('user'), accessToken)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename ?? 'terms-and-conditions.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      setHasDownloaded(true)
    } catch (err) {
      setLegalError(getErrorMessage(err, 'Download failed — please try again'))
    } finally {
      setDownloading(false)
    }
  }

  const handleAgree = async () => {
    setLegalError(null)
    try {
      await agreeToTerms().unwrap()
      setStep('interests')
    } catch (err) {
      setLegalError(getErrorMessage(err, 'Failed to record your agreement — please try again'))
    }
  }

  const handleFinish = (goNew = false) => {
    finishOnboarding()
    if (selectedSlugs.length > 0) {
      void updatePreferences({ interests: selectedSlugs })
    }
    if (goNew) void navigate('/new')
  }

  if (!visible) return null

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10001,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        animation: 'obFade 0.2s ease',
      }}
    >
      <style>{`
        @keyframes obFade  { from { opacity:0 } to { opacity:1 } }
        @keyframes obSlide { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }
      `}</style>

      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: '20px',
        padding: '1.35rem 2.5rem 2.1rem',
        maxWidth: '480px',
        width: '100%',
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        animation: 'obSlide 0.25s ease',
        position: 'relative',
      }}>

        {/* ── Step 1: Welcome (includes the mandatory terms gate, when required) ── */}
        {step === 'welcome' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: needsAgreement ? '2.5rem' : '3.5rem', marginBottom: '0.6rem' }}>👋</div>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: needsAgreement ? '1.3rem' : '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              Welcome to Ekkam AI, {user?.displayName?.split(' ')[0]}!
            </h2>
            <p style={{ margin: needsAgreement ? '0 0 1.1rem' : '0 0 1.75rem', color: 'var(--text-muted)', lineHeight: 1.6, fontSize: '0.925rem' }}>
              Ask anything. Our network of human experts and custom AI agents is ready to respond, discuss, and deliver the precise answers you are looking for.
            </p>

            {needsAgreement ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--accent-primary)' }}>🔒 Action required</span>
                </div>

                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.65rem', textAlign: 'left',
                  marginBottom: '1.1rem', padding: '0.75rem 0.85rem', borderRadius: 10,
                  background: agreedCheckbox ? 'rgba(124,58,237,0.08)' : 'transparent',
                  border: `1px solid ${agreedCheckbox ? 'rgba(124,58,237,0.35)' : 'var(--border)'}`,
                  cursor: hasDownloaded ? 'pointer' : 'not-allowed',
                  opacity: hasDownloaded ? 1 : 0.6,
                  transition: 'all 0.15s',
                }}>
                  <input
                    type="checkbox"
                    checked={agreedCheckbox}
                    disabled={!hasDownloaded}
                    onChange={(e) => setAgreedCheckbox(e.target.checked)}
                    style={{ marginTop: '0.15rem', flexShrink: 0, width: 16, height: 16, accentColor: 'var(--accent-primary)', cursor: hasDownloaded ? 'pointer' : 'not-allowed' }}
                  />
                  <span style={{ fontSize: '0.85rem', lineHeight: 1.45, color: agreedCheckbox ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                    I have downloaded and agree to the{' '}
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!downloading) void handleDownload() }}
                      style={{
                        color: 'var(--accent-primary)', fontWeight: 600,
                        textDecoration: 'underline', textUnderlineOffset: '2px',
                        cursor: downloading ? 'not-allowed' : 'pointer',
                        pointerEvents: downloading ? 'none' : 'auto',
                      }}
                    >
                      Terms &amp; Conditions
                    </a>
                    {hasDownloaded && <span style={{ color: 'var(--success)' }}> ✓</span>}
                  </span>
                </label>

                {legalError && (
                  <div style={{ marginBottom: '1rem', padding: '0.6rem 0.75rem', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', fontSize: '0.8rem', color: 'var(--error)', textAlign: 'left' }}>
                    {legalError}
                  </div>
                )}

                <button
                  onClick={() => void handleAgree()}
                  disabled={!agreedCheckbox || agreeing}
                  style={{
                    width: '100%', padding: '0.75rem', borderRadius: '10px', fontWeight: 600, fontSize: '0.95rem',
                    cursor: (!agreedCheckbox || agreeing) ? 'not-allowed' : 'pointer',
                    background: (!agreedCheckbox || agreeing) ? 'transparent' : 'var(--accent-primary)',
                    color: (!agreedCheckbox || agreeing) ? 'var(--text-muted)' : 'white',
                    border: (!agreedCheckbox || agreeing) ? '1px solid var(--border)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  {agreeing ? 'Saving…' : 'Agree & Continue →'}
                </button>
              </>
            ) : (
              <>
                <div style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: '10px', padding: '0.65rem 1rem', marginBottom: '1.25rem', fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  🎯 You can set your interests at any time from <strong style={{ color: 'var(--accent-primary)' }}>Settings → Profile</strong> to personalise your feed.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  <button
                    onClick={() => setStep('interests')}
                    style={{ width: '100%', padding: '0.75rem', background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.9' }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
                  >
                    Get started →
                  </button>
                  <button
                    onClick={() => finishOnboarding()}
                    style={{ width: '100%', padding: '0.65rem', background: 'transparent', color: 'var(--text-muted)', border: 'none', borderRadius: '10px', fontSize: '0.875rem', cursor: 'pointer' }}
                  >
                    Don't show again
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Step 2: Pick interests ── */}
        {step === 'interests' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🎯</div>
              <h2 style={{ margin: '0 0 0.4rem', fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                What are you interested in?
              </h2>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                Pick a few topics to explore first
              </p>
            </div>
            <div style={{ marginBottom: '1.75rem' }}>
              <MultiSelectDropdown
                options={categories.map(c => ({ value: c.slug, label: c.name, icon: c.icon ?? undefined, color: c.color ?? undefined }))}
                selected={selectedSlugs}
                onChange={setSelectedSlugs}
                placeholder="Select topics you're interested in"
                searchPlaceholder="Search topics..."
                align="left"
                fullWidth
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setStep('welcome')}
                style={{ flex: 1, padding: '0.7rem', background: 'transparent', border: '1px solid var(--border)', borderRadius: '10px', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.875rem' }}
              >← Back</button>
              <button
                onClick={() => setStep('done')}
                disabled={selectedSlugs.length === 0}
                style={{ flex: 2, padding: '0.7rem', background: selectedSlugs.length === 0 ? 'transparent' : 'var(--accent-primary)', color: selectedSlugs.length === 0 ? 'var(--text-muted)' : 'white', border: selectedSlugs.length === 0 ? '1px solid var(--border)' : 'none', borderRadius: '10px', fontWeight: 600, fontSize: '0.875rem', cursor: selectedSlugs.length === 0 ? 'not-allowed' : 'pointer' }}
              >Continue →</button>
            </div>
            <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', background: 'var(--bg-elevated)', borderRadius: '10px', textAlign: 'center' }}>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Don't find what you are looking for? Don't worry, sign up without any selection. We will figure out things for you.
              </p>
              <button
                onClick={() => handleFinish(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: '0.25rem 0', textDecoration: 'underline', textUnderlineOffset: '2px' }}
              >
                Skip and continue →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Done ── */}
        {step === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🚀</div>
            <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              You're all set!
            </h2>
            <p style={{ margin: '0 0 1.75rem', color: 'var(--text-muted)', lineHeight: 1.6, fontSize: '0.9rem' }}>
              Ask your first question and watch our AI agents respond in real time.
            </p>
            <div style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: '10px', padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              🎯 Want to update your interests later? Go to <strong style={{ color: 'var(--accent-primary)' }}>Settings → Profile</strong>.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <button
                onClick={() => handleFinish(true)}
                style={{ width: '100%', padding: '0.75rem', background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' }}
              >✏️ Ask my first question</button>
              {selectedSlugs.length > 0 && (
                <button
                  onClick={() => {
                    finishOnboarding()
                    void updatePreferences({ interests: selectedSlugs })
                    void navigate(`/?categorySlugs=${selectedSlugs[0]}`)
                  }}
                  style={{ width: '100%', padding: '0.7rem', background: 'transparent', border: '1px solid var(--border)', borderRadius: '10px', color: 'var(--text-secondary)', fontSize: '0.875rem', cursor: 'pointer' }}
                >Browse {categories.find(c => c.slug === selectedSlugs[0])?.name ?? 'topics'} →</button>
              )}
              <button
                onClick={() => handleFinish(false)}
                style={{ width: '100%', padding: '0.65rem', background: 'transparent', color: 'var(--text-muted)', border: 'none', fontSize: '0.875rem', cursor: 'pointer' }}
              >Explore on my own</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
